import { getApiBaseUrl } from '../../utils/environment.mjs';
import axios from 'axios';
import { secureLogger } from '../../utils/secureLogger.mjs';
import { formatLocalDate } from '../../utils/dateOnly.mjs';

const API_URL = getApiBaseUrl();

/**
 * Send a line item's date as a plain YYYY-MM-DD.
 *
 * DateInput hands the form a LOCAL-midnight Date, and JSON.stringify turns a
 * Date into a UTC timestamp - so east of UTC an item dated 31 Aug went over the
 * wire as "2026-08-30T21:00:00.000Z" and the DATEONLY column stored the 30th.
 * A date-only string means the same calendar day in every timezone.
 *
 * Applied in the service rather than at each call site so all three save paths
 * (create, update, admin update) are covered by one rule.
 *
 * Two more wire rules live here for the same reason:
 *  - A blank amount goes as null. Rows the invoice reader could not price are
 *    left blank rather than 0, and '' is not a number the DECIMAL column
 *    accepts, so a draft with an unfilled amount would fail to save.
 *  - `extractionStatus`, `receiptNumberFromDate` and `expenditureDateIsDefault`
 *    only drive the form's per-row notes and the invoice merge. They are not
 *    claim data, so they never leave the browser.
 */
const withDateOnly = (items) =>
  (Array.isArray(items) ? items : []).map(item => {
    if (!item || typeof item !== 'object') return item;
    const {
      extractionStatus: _status,
      receiptNumberFromDate: _fromDate,
      expenditureDateIsDefault: _defaultDate,
      ...rest
    } = item;
    if (rest.expenditureDate) rest.expenditureDate = formatLocalDate(rest.expenditureDate);
    if (rest.receiptAmount === '') rest.receiptAmount = null;
    return rest;
  });

class ExpenseService {
  async getExpenseClaim(claimId) {
    const response = await axios.get(`${API_URL}/expenses/claims/${claimId}`, {
      withCredentials: true,
    });

    
    return response.data.data;
  }
  
  async saveExpenseClaim(claimId, items, isDraft = true) {
    const response = await axios.post(`${API_URL}/expenses/claim`, {
      claimId,
      items: withDateOnly(items),
      status: isDraft ? 'draft' : 'submitted'
    }, {
      withCredentials: true,
    });
    
    return response.data;
  }

  async updateExpenseItem(itemId, updates) {
    const response = await axios.put(`${API_URL}/expenses/item/${itemId}`, updates, {
      withCredentials: true,
    });
    
    return response.data.item;
  }

  
  
  async deleteExpenseItem(itemId) {
    const response = await axios.delete(`${API_URL}/expenses/item/${itemId}`, {
      withCredentials: true,
    });
    
    return response.data;
  }
  
  async addExpenseItem(claimId) {
    const response = await axios.post(`${API_URL}/expenses/item`, {
      claimId
    }, {
      withCredentials: true,
    });
    
    return response.data.item;
  }
  
  async getExchangeRate(currency, date) {
    const response = await axios.get(`${API_URL}/expenses/exchange-rate`, {
      params: {
        currency,
        date: date ? date.toISOString() : undefined
      },
      withCredentials: true,
    });
    
    return response.data.rate;
  }
  
  async searchCities(query) {
    const response = await axios.get(`${API_URL}/locations/search`, {
      params: { query },
      withCredentials: true,
    });
    
    return response.data.results;
  }


  async adminUpdateExpenseClaim (claimId, claimData, isDraft = false) {
    try {
      const response = await axios.put(
        `${API_URL}/admin/claims/expense/${claimId}/update`,
        {
          ...claimData,
          // The admin payload nests its lines under `items` too.
          ...(claimData?.items ? { items: withDateOnly(claimData.items) } : {}),
          status: isDraft ? 'draft' : 'submitted',
          isAdminEdit: true
        },
        {
          withCredentials: true
        }
      );
      return response.data;
    } catch (error) {
      secureLogger.error('Error updating expense claim as admin', { status: error.response?.status });
      throw new Error(error.response?.data?.message || 'Failed to update claim as admin');
    }
  }

  
  // Admin pulls back their own approval before the auditor picks the claim up.
  async withdrawApproval(claimId, comments = '') {
    const response = await axios.post(
      `${API_URL}/admin/claims/expense/${claimId}/withdraw`,
      { comments },
      { withCredentials: true }
    );
    return response.data;
  }

  async submitAdminAction(claimId, actionData) {
    try {
      const response = await axios.post(
        `${API_URL}/admin/claims/${claimId}/expense/action`,
        actionData,
        {
          withCredentials: true, 
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    } catch (error) {
      secureLogger.error('Admin action submission error', { status: error.response?.status });
      throw error;
    }
  }

  
  
  async updateExpenseClaim(claimId, items, isDraft = false) {
    try {
      
      const response = await axios.put(`${API_URL}/expenses/claim/${claimId}`, {
        items: withDateOnly(items),
        isDraft,
        status: isDraft ? 'draft' : 'submitted'
      }, {
        withCredentials: true,
      });
      
      return response.data;
    } catch (error) {
      secureLogger.error('Error updating expense claim', { status: error.response?.status });
      throw error;
    }
  }



}

const expenseService = new ExpenseService();
export default expenseService;

